<template>
	<t-input-wrapper
		ref="inputWrapper"
		:label="label"
		:description="description"
		:hint="hint"
		:isInvalid="isInvalid"
		:validationMessage="validationMessage"
		:warningMessage="warningMessage"
	>
		<div
			ref="jsonEditor"
			:style="getStyle"
			:class="[{invalid: isInvalid, disabled: disabled}]"
			:placeholder="placeholder"
			:disabled="disabled"
			:aria-disabled="disabled"
			class="text-area"
			spellcheck="false"
			v-on="inputListeners"
			@keydown="onKeyDown"
		/>
	</t-input-wrapper>
</template>

<script>

import * as monaco from 'monaco-editor';
import formMixin from '../../inputs/formMixin';
import inputWrapperMixin from '../../inputs/inputWrapperMixin';
import TInputWrapper from '../../inputs/TInputWrapper';
export default {
	name:'TJsonEditor',
	components: { TInputWrapper },
	mixins: [ formMixin, inputWrapperMixin ],
	props: {
		jsonObject: {
			type: Object,
			default: null
		},
		height: {
			type: String,
			default: '80px'
		},
		minHeight: {
			type: String,
			default: '80px'
		},
		maxHeight: {
			type: String,
			default: 'none',
		},
		resize: {
			type: String,
			default: 'both',
			validator: (value) => [ 'both', 'horizontal', 'vertical', 'none' ].indexOf(value) !== -1,
		},
		autoFocus: {
			type: Boolean,
			default: false
		},
		overflow: {
			type: String,
			default: 'hidden'
		}
	},
	data() {
		return {
			jsonString: null,
			editor: null
		};
	},
	computed: {
		getStyle() {
			return {
				width: this.width,
				paddingLeft: this.paddingLeft,
				resize: this.resize,
				height: this.height,
				minHeight: this.minHeight,
				maxHeight: this.maxHeight,
				overflow: this.overflow,
			};
		},
		inputListeners() {
			return {
				...this.$listeners,
				input: () => {
					this.$emit('input', this.editor.getValue());
				},
				change: () => {
					this.$emit('change', this.editor.getValue());
				}
			};
		},
	},
	watch: {
		jsonObject: function() {
			this.setJsonString();
		},
		isInvalid: function() {
			this.updateHeight();
		}
	},
	updated() {
		this.updateHeight();
	},
	mounted() {
		let el = this.$refs.jsonEditor;
		this.editor = monaco.editor.create(el, {
			language: 'json'
		});
		if (this.autoFocus) {
			document.activeElement.blur();
			this.$refs.textarea.focus();
		}
		this.setJsonString();
		this.editor.onDidChangeModelContent( () => {
			this.$emit('change', this.editor.getValue());
		});
	},
	beforeDestroy() {
		this.editor && this.editor.dispose();
	},
	methods: {
		updateHeight() {
			let container = this.$refs.jsonEditor;
			let jsonEditor = this.editor;
			if (!container || !jsonEditor) return;
			let contentWidth = container.clientWidth;
			let contentHeight = container.clientHeight;
			if (this.isInvalid) contentHeight -= 26;
			try {
				jsonEditor.layout({ width: contentWidth, height: contentHeight });
			} catch (e) {
				//
			}
		},
		setJsonString() {
			if (this.jsonObject) {
				let jsonString = JSON.stringify(this.jsonObject, null, '\t');
				this.jsonString = jsonString;
				if (this.editor && this.jsonString !== this.editor.getValue()) {
					this.editor.setValue(this.jsonString);
				}
			}
		},
		onKeyDown(event) {
			let specialKeys = [];
			if (specialKeys.includes(event.code)) {
				event.stopPropagation();
				event.preventDefault();
				this[`on${event.code}`]();
			}
		}
	}
};
</script>

<style>

</style>

<docs>
## Examples
```jsx noeditor
new Vue({
	data() {
		return {
			jsonObject: {
				foo: {
					bar: 'baz'
				}
			}
		};
	},
	template: `
		<t-json-editor :jsonObject="jsonObject" width="100%" height="200px" />
	`
});
```

```html
<t-json-editor :jsonObject="jsonObject" />
```
</docs>
